import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { newIdempotencyId, parseConvexError } from "../lib/utils";
import { hasConvexConfig, hasWorkOsConfig } from "../lib/config";

export function SuppliersPage() {
  if (!hasConvexConfig || !hasWorkOsConfig) {
    return (
      <div className="page">
        <h1>Suppliers</h1>
        <p className="lede">Configure auth to manage suppliers.</p>
      </div>
    );
  }
  return <SuppliersAuthenticated />;
}

function SuppliersAuthenticated() {
  const [search, setSearch] = useState("");
  const suppliers = useQuery(api.suppliers.list, { search: search || undefined });
  const create = useMutation(api.suppliers.create);
  const [draft, setDraft] = useState({ name: "", code: "", email: "", leadTimeDays: "" });
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Suppliers</h1>
          <p className="lede">Supplier directory linked to items and purchase orders.</p>
        </div>
      </div>

      <div className="toolbar">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search suppliers…"
        />
      </div>

      <section className="panel" style={{ padding: 0, marginBottom: "1rem" }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Email</th>
              <th>Lead time</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(suppliers ?? []).map((s) => (
              <tr key={s._id}>
                <td>{s.name}</td>
                <td className="mono">{s.code ?? "—"}</td>
                <td>{s.email ?? "—"}</td>
                <td>{s.leadTimeDays != null ? `${s.leadTimeDays}d` : "—"}</td>
                <td>{s.isActive ? "Active" : "Inactive"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="panel">
        <h2 className="panel-title">Add supplier</h2>
        <div className="header-fields">
          <label className="field">
            <span>Name</span>
            <input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Code</span>
            <input
              value={draft.code}
              onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
            />
          </label>
        </div>
        <div className="header-fields">
          <label className="field">
            <span>Email</span>
            <input
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Lead time (days)</span>
            <input
              value={draft.leadTimeDays}
              onChange={(e) => setDraft((d) => ({ ...d, leadTimeDays: e.target.value }))}
            />
          </label>
        </div>
        {error && <p className="error">{error}</p>}
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            void (async () => {
              try {
                setError(null);
                await create({
                  name: draft.name,
                  code: draft.code || undefined,
                  email: draft.email || undefined,
                  leadTimeDays: draft.leadTimeDays ? Number(draft.leadTimeDays) : undefined,
                  idempotencyId: newIdempotencyId("sup"),
                });
                setDraft({ name: "", code: "", email: "", leadTimeDays: "" });
              } catch (err) {
                setError(parseConvexError(err).message);
              }
            })();
          }}
        >
          Add supplier
        </button>
      </section>
    </div>
  );
}
